import { Button, Popover } from 'antd';
import { AlertTriangle } from 'lucide-react';
import {
    chipBackground,
    readableTagColor,
    tagShortName,
    type MatrixRow,
    type TagOption,
} from './types';

export interface DeadEnd {
    tagId: number;
    /** Các trạng thái đang cho phép chuyển tới thẻ này */
    sources: number[];
    /** Có hàng trong ma trận nhưng không đích nào đang bật (khác với không có hàng) */
    hasRow: boolean;
}

/**
 * Tìm những trạng thái khách vào được nhưng không đi tiếp được:
 * có ít nhất một bước chuyển đang bật trỏ tới, mà bản thân lại không có
 * đích nào đang bật. Gồm cả trường hợp thẻ không hề có hàng trong ma trận.
 */
export function findDeadEnds(rows: MatrixRow[]): DeadEnd[] {
    const outgoing = new Map<number, number>();
    rows.forEach((r) =>
        outgoing.set(r.fromTagId, r.dests.filter((d) => d.isAllowed).length)
    );

    const incoming = new Map<number, number[]>();
    rows.forEach((r) =>
        r.dests.forEach((d) => {
            if (!d.isAllowed) return;
            const list = incoming.get(d.toTagId) ?? [];
            list.push(r.fromTagId);
            incoming.set(d.toTagId, list);
        })
    );

    const out: DeadEnd[] = [];
    incoming.forEach((sources, tagId) => {
        if ((outgoing.get(tagId) ?? 0) > 0) return;
        out.push({ tagId, sources, hasRow: outgoing.has(tagId) });
    });
    return out.sort((a, b) => b.sources.length - a.sources.length);
}

interface Props {
    deadEnds: DeadEnd[];
    tagsById: Record<number, TagOption>;
    isDark: boolean;
    canEdit: boolean;
    onAddRow: (tagId: number) => void;
    onFocusRow: (tagId: number) => void;
}

function TagBadge({ tag, id, isDark }: { tag?: TagOption; id: number; isDark: boolean }) {
    return (
        <span
            className="font-mono text-[11px] px-1.5 py-0.5 rounded shrink-0"
            style={{
                backgroundColor: chipBackground(tag?.color, isDark),
                color: readableTagColor(tag?.color, isDark),
            }}
        >
            {tag?.code || tag?.name || `#${id}`}
        </span>
    );
}

/** Badge ở thanh công cụ, mở ra danh sách trạng thái bị kẹt kèm lối sửa nhanh. */
export default function DeadEndNotice({
    deadEnds,
    tagsById,
    isDark,
    canEdit,
    onAddRow,
    onFocusRow,
}: Props) {
    if (!deadEnds.length) return null;

    const content = (
        <div className="w-[330px]">
            <div className="pb-2 border-b border-gray-100 dark:border-[#2a2a2a]">
                <div className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">
                    Trạng thái không có lối ra
                </div>
                <div className="text-[11px] text-gray-400 dark:text-[#8a8a8a] mt-0.5 leading-relaxed">
                    Khách chuyển vào được nhưng không chuyển đi đâu được. Trạng thái kết thúc
                    như “Hoàn Thành” nằm ở đây là bình thường.
                </div>
            </div>

            <div className="max-h-[280px] overflow-y-auto py-1.5">
                {deadEnds.map((d) => {
                    const tag = tagsById[d.tagId];
                    return (
                        <div
                            key={d.tagId}
                            className="px-2 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-[#262626]"
                        >
                            <div className="flex items-center gap-2">
                                <TagBadge tag={tag} id={d.tagId} isDark={isDark} />
                                <span className="text-xs text-gray-700 dark:text-gray-200 truncate">
                                    {tagShortName(tag, d.tagId)}
                                </span>
                            </div>

                            <div className="flex items-center gap-1.5 flex-wrap mt-1.5 pl-0.5">
                                <span className="text-[11px] text-gray-400 dark:text-[#8a8a8a]">
                                    Nhận từ
                                </span>
                                {d.sources.map((s) => (
                                    <TagBadge key={s} tag={tagsById[s]} id={s} isDark={isDark} />
                                ))}
                            </div>

                            {canEdit && (
                                <div className="mt-1.5">
                                    {d.hasRow ? (
                                        <Button
                                            size="small"
                                            type="link"
                                            className="!px-0 !h-auto !text-[11px]"
                                            onClick={() => onFocusRow(d.tagId)}
                                        >
                                            Tới hàng của trạng thái này
                                        </Button>
                                    ) : (
                                        <Button
                                            size="small"
                                            type="link"
                                            className="!px-0 !h-auto !text-[11px]"
                                            onClick={() => onAddRow(d.tagId)}
                                        >
                                            Thêm hàng để cấu hình lối ra
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <Popover trigger="click" placement="bottomRight" content={content}>
            <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
                           border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100
                           dark:border-[#5c4415] dark:!text-[#f5cd76] dark:bg-[#3a2a0e] dark:hover:bg-[#4a360f]
                           transition-colors"
            >
                <AlertTriangle className="w-3.5 h-3.5" />
                {deadEnds.length} trạng thái không có lối ra
            </button>
        </Popover>
    );
}
